import api from "./api";

const financeService = {
  // Dashboard
  getFinancialDashboard: (params) => api.get("/finance/dashboard", { params }),

  // Accounts
  getAccounts: (params) => api.get("/finance/accounts", { params }),
  getAccountById: (id) => api.get(`/finance/accounts/${id}`),
  createAccount: (accountData) => api.post("/finance/accounts", accountData),
  updateAccount: (id, accountData) =>
    api.put(`/finance/accounts/${id}`, accountData),
  deleteAccount: (id) => api.delete(`/finance/accounts/${id}`),
  getAccountBalanceHistory: (id, params) =>
    api.get(`/finance/accounts/${id}/balance-history`, { params }),

  // Transactions
  getTransactions: (params) => api.get("/finance/transactions", { params }),
  getTransactionById: (id) => api.get(`/finance/transactions/${id}`),
  createTransaction: (transactionData) =>
    api.post("/finance/transactions", transactionData),
  updateTransaction: (id, transactionData) =>
    api.put(`/finance/transactions/${id}`, transactionData),
  deleteTransaction: (id) => api.delete(`/finance/transactions/${id}`),
  approveTransaction: (id) => api.patch(`/finance/transactions/${id}/approve`),
  getPendingGMDTransactions: (params) => 
    api.get("/finance/transactions/pending-gmd", { params }),
  approveGMDTransaction: (id, data) => 
    api.patch(`/finance/transactions/${id}/gmd-approve`, data),
  rejectGMDTransaction: (id, data) => 
    api.patch(`/finance/transactions/${id}/gmd-reject`, data),
  getApprovedTransactions: (params = {}) => 
    api.get("/finance/transactions/approved", { params }),
  getFinanceApprovedTransactions: (params = {}) => 
    api.get("/finance/transactions/finance-approved", { params }),

  // Imprest Management
  getImprestExpenses: (transactionId) =>
    api.get(`/finance/imprest/${transactionId}/expenses`),
  createImprestExpense: (transactionId, expenseData) =>
    api.post(`/finance/imprest/${transactionId}/expenses`, expenseData),
  submitImprestRetirement: (transactionId) =>
    api.post(`/finance/imprest/${transactionId}/retire`),

  // Budgets
  getBudgets: (params) => api.get("/finance/budgets", { params }),
  getBudgetById: (id) => api.get(`/finance/budgets/${id}`),
  getBudgetsWithBalances: (params) =>
    api.get("/finance/budgets/with-balances", { params }),
  getBudgetBalance: (id) => api.get(`/finance/budgets/${id}/balance`),
  createBudget: (budgetData) => api.post("/finance/budgets", budgetData),
  updateBudget: (id, budgetData) =>
    api.put(`/finance/budgets/${id}`, budgetData),
  deleteBudget: (id) => api.delete(`/finance/budgets/${id}`),

  // Subsidiaries
  getSubsidiaries: () => api.get("/finance/subsidiaries"),

  // Budget Items
  getBudgetItems: (budgetId) => api.get(`/finance/budgets/${budgetId}/items`),
  createBudgetItem: (budgetId, itemData) =>
    api.post(`/finance/budgets/${budgetId}/items`, itemData),
  updateBudgetItem: (itemId, itemData) =>
    api.put(`/finance/budget-items/${itemId}`, itemData),
  deleteBudgetItem: (itemId) => api.delete(`/finance/budget-items/${itemId}`),

  // Reports
  getAvailableReports: () => api.get("/finance/reports"),
  getProfitLossReport: (params) =>
    api.get("/finance/reports/profit-loss", { params }),
  getBalanceSheetReport: (params) =>
    api.get("/finance/reports/balance-sheet", { params }),
  getCashFlowReport: (params) =>
    api.get("/finance/reports/cash-flow", { params }),

  // Subsidiaries
  getSubsidiaries: () => api.get("/finance/subsidiaries"),

  // Expense Management (Finance Approval)
  getApprovedExpenses: (params) =>
    api.get("/finance/expenses/approved", { params }),
  getPendingFinanceExpenses: (params) =>
    api.get("/finance/expenses/pending", { params }),
  getExpenseById: (id) => api.get(`/finance/expenses/${id}`),
  approveExpensePayment: (id, approvalData) =>
    api.patch(`/finance/expenses/${id}/approve-payment`, approvalData),
  rejectExpensePayment: (id, rejectionData) =>
    api.patch(`/finance/expenses/${id}/reject-payment`, rejectionData),
  processExpensePayment: (id, paymentData) =>
    api.patch(`/finance/expenses/${id}/process-payment`, paymentData),
  getExpensePaymentHistory: (params) =>
    api.get("/finance/expenses/payment-history", { params }),
  getExpenseStats: () => api.get("/finance/expenses/stats"),
  getApprovedExpensesList: (params) =>
    api.get("/finance/expenses/approved-list", { params }),

  // Payroll Approval (Finance Final Approval)
  getPendingPayrollApprovals: (params) =>
    api.get("/finance/payroll/pending-approvals", { params }),
  getPayrollApprovalHistory: (params) =>
    api.get("/finance/payroll/approval-history", { params }),
  getPayrollById: (id) => api.get(`/finance/payroll/${id}`),
  approvePayroll: (id, approvalData) =>
    api.patch(`/finance/payroll/${id}/approve`, approvalData),
  rejectPayroll: (id, rejectionData) =>
    api.patch(`/finance/payroll/${id}/reject`, rejectionData),
  getPayrollApprovalStats: () => api.get("/finance/payroll/approval-stats"),
  bulkApprovePayroll: (approvalData) =>
    api.post("/finance/payroll/bulk-approve", approvalData),
  getPayrollPeriodDetails: (periodId) =>
    api.get(`/finance/payroll/period/${periodId}`),
  getPayrollSummaryForApproval: (periodId) =>
    api.get(`/finance/payroll/period/${periodId}/summary`),
  authorizePayrollPayment: (id, authorizationData) =>
    api.patch(`/finance/payroll/${id}/authorize-payment`, authorizationData),
  getPayrollPaymentQueue: (params) =>
    api.get("/finance/payroll/payment-queue", { params }),
  processPayrollPayments: (paymentData) =>
    api.post("/finance/payroll/process-payments", paymentData),

  // Utility functions
  getAccountTypes: () =>
    Promise.resolve({
      data: {
        data: {
          accountTypes: [
            { value: "asset", label: "Asset", normalBalance: "debit" },
            { value: "liability", label: "Liability", normalBalance: "credit" },
            { value: "equity", label: "Equity", normalBalance: "credit" },
            { value: "revenue", label: "Revenue", normalBalance: "credit" },
            { value: "expense", label: "Expense", normalBalance: "debit" },
          ],
        },
      },
    }),

  getTransactionTypes: () =>
    Promise.resolve({
      data: {
        data: {
          transactionTypes: [
            { value: "journal", label: "Journal Entry" },
            { value: "payment", label: "Payment" },
            { value: "receipt", label: "Receipt" },
            { value: "transfer", label: "Transfer" },
            { value: "adjustment", label: "Adjustment" },
          ],
        },
      },
    }),

  getBudgetTypes: () =>
    Promise.resolve({
      data: {
        data: {
          budgetTypes: [
            { value: "annual", label: "Annual Budget" },
            { value: "quarterly", label: "Quarterly Budget" },
            { value: "monthly", label: "Monthly Budget" },
            { value: "project", label: "Project Budget" },
            { value: "department", label: "Department Budget" },
          ],
        },
      },
    }),

  getCurrencies: () =>
    Promise.resolve({
      data: {
        data: {
          currencies: [
            { value: "NGN", label: "Nigerian Naira (N)" },
            { value: "EUR", label: "Euro (€)" },
            { value: "GBP", label: "British Pound (£)" },
            { value: "JPY", label: "Japanese Yen (¥)" },
            { value: "CAD", label: "Canadian Dollar (₦)" },
            { value: "AUD", label: "Australian Dollar (₦)" },
          ],
        },
      },
    }),

  // Utility formatting functions
  formatCurrency: (amount, currency = "NGN") => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return currency === "NGN" ? "₦0.00" : "₦0.00";
    }

    const numAmount = parseFloat(amount);

    if (currency === "NGN") {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        minimumFractionDigits: 2,
      }).format(numAmount);
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "NGN",
      minimumFractionDigits: 2,
    }).format(numAmount);
  },

  calculatePercentage: (value, total) => {
    if (!total || total === 0 || !value) return 0;
    return Math.round((parseFloat(value) / parseFloat(total)) * 100);
  },

  formatDate: (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  },

  formatNumber: (number, decimals = 2) => {
    if (number === null || number === undefined || isNaN(number)) {
      return "0.00";
    }
    return parseFloat(number).toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  },

  getAccountTypeColor: (type) => {
    const colors = {
      asset: "bg-green-100 text-green-800",
      liability: "bg-red-100 text-red-800",
      equity: "bg-blue-100 text-blue-800",
      revenue: "bg-purple-100 text-purple-800",
      expense: "bg-orange-100 text-orange-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  },

  // Accounts Payable & Receivable
  getPayables: (params) => api.get("/finance/payables", { params }),
  getPayableById: (id) => api.get(`/finance/payables/${id}`),
  createPayable: (payableData) => api.post("/finance/payables", payableData),
  updatePayableAmount: (id, total_amount) => 
    api.put(`/finance/payables/${id}/amount`, { total_amount }),
  linkPurchaseRequestToPayable: (payableId, purchase_request_id) =>
    api.post(`/finance/payables/${payableId}/link-pr`, { purchase_request_id }),
  unlinkPurchaseRequestFromPayable: (payableId, prId) =>
    api.delete(`/finance/payables/${payableId}/unlink-pr/${prId}`),
  getAvailablePurchaseRequests: () => api.get("/finance/payables/available-prs"),
  recordPayablePayment: (id, paymentData) =>
    api.post(`/finance/payables/${id}/payment`, paymentData),
  getPayablePaymentHistory: (id) => api.get(`/finance/payables/${id}/payments`),

  getReceivables: (params) => api.get("/finance/receivables", { params }),
  getReceivableById: (id) => api.get(`/finance/receivables/${id}`),
  createReceivable: (receivableData) =>
    api.post("/finance/receivables", receivableData),
  recordReceivablePayment: (id, paymentData) =>
    api.post(`/finance/receivables/${id}/payment`, paymentData),
  getReceivablePaymentHistory: (id) =>
    api.get(`/finance/receivables/${id}/payments`),

  getAPARSummary: (params) => api.get("/finance/ap-ar/summary", { params }),
  getAPARSubsidiaryBreakdown: () => api.get("/finance/ap-ar/subsidiary-breakdown"),
};

export { financeService };
export default financeService;
